import {putAll, takeLatestF} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {isFull, none, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {
    PagedModelUserProfileSubjectRoleManagementDtoBreederRoleManagement,
} from 'api/gen/PagedModelUserProfileSubjectRoleManagementDtoBreederRoleManagement';
import {tCommon} from 'app/translations';
import {showBeError} from 'utils/show-be-error';
import {
    convertTableDataParamsToDto,
    getRequestedTableColumnSettings,
    getRequestedTablePageParams,
    simpleTableSelector,
    tablesAction,
} from 'utils/tables';

import {adminAction, GetManagementBreedersTableDataAction} from '../action';

const tableId = 'managementBreeders';
const blankPageModel: PagedModelUserProfileSubjectRoleManagementDtoBreederRoleManagement = {
    content: [],
    page: {
        number: 1,
        size: 1,
        totalElements: 1,
        totalPages: 1,
    },
};

// eslint-disable-next-line max-lines-per-function
function* execute(action: GetManagementBreedersTableDataAction): SagaIterator {
    const {tableDataRequest, breederId} = action.payload;
    if (tableDataRequest.isEmpty) {
        return yield* put(adminAction.getManagementBreedersTableDataSuccess(none));
    }

    const tableState = yield* select(simpleTableSelector.tableState(tableId));
    const tableDataParams = getRequestedTablePageParams({
        tableId,
        tableDataRequest: tableDataRequest.orCrash('No tableDataRequest for management breeders table'),
        tableState,
        resetType: 'tableDataParams',
    });

    if (breederId.isEmpty) {
        yield* put(adminAction.getManagementBreedersTableDataSuccess(opt(blankPageModel)));
        return yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    }
    yield* put(tablesAction.tableDataFetchStart(tableId));

    const columnSettings = getRequestedTableColumnSettings<'managementBreeders'>(tableState);
    const {filters, params} = convertTableDataParamsToDto({tableDataParams, columnSettings});
    const correctedFilter = opt(filters)
        .mapIn(x => (x.tag === 'CONTAINS_STRING' && x.field === 'username') ? x.value : null)
        .filterIn(isFull)
        .headIn()
        .orNull();
    const correctedParams = {sort: opt(params.sort).orElse([]), usernameFilterText: correctedFilter};

    const res = yield* call(
        Api.getSubjectBreederManagementPage,
        correctedParams,
        {breederId: breederId.orCrash('Breeder ID not found')},
    );

    if (res.isSuccess) {
        const {headers, pagedModel} = res.data;
        yield* put(adminAction.getManagementBreedersTableDataSuccess(opt(pagedModel)));
        yield* put(adminAction.setBreedersExtraColumns(headers === null ? [] : headers));
        yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    } else {
        yield putAll(showBeError(res, tCommon('tableLoadErrorTitle')));
        yield* put(tablesAction.tableDataFetchFailure(tableId));
    }
}
/** Runs the admin saga that loads breeder-management table data. */
export function* getManagementBreederTableData(): SagaIterator {
    yield takeLatestF('admin/GET_MANAGEMENT_BREEDERS_TABLE_DATA', execute);
}
